import type { MedicationDose, MedicationMealRelation } from '@/api/model';

// 健康方案状态展示映射（两态模型 + 会诊锁徽章）
// 状态机仅 draft / published 两态；in_consultation=true 时草稿在 UI 上额外展示「会诊中」徽章。
// theme 用 HeroUI Chip 的 color 语义（accent/success/warning/danger/default）

// Chip 语义色,供各展示映射复用
type ChipColor = 'default' | 'accent' | 'success' | 'warning' | 'danger';

export const planStatusMap: Record<
  string,
  {
    text: string;
    theme: ChipColor;
  }
> = {
  draft: { text: '草稿', theme: 'default' },
  published: { text: '已发布', theme: 'success' },
};

export const planStatusOptions = [
  { label: '全部', value: '' },
  ...Object.entries(planStatusMap).map(([value, cfg]) => ({
    label: cfg.text,
    value,
  })),
];

// 会诊紧急程度展示
export const consultUrgencyMap: Record<
  string,
  {
    text: string;
    theme: ChipColor;
  }
> = {
  urgent: { text: '紧急', theme: 'danger' },
  normal: { text: '普通', theme: 'warning' },
  routine: { text: '例行', theme: 'default' },
};

// 监测类型展示
export const monitoringTypeMap: Record<string, string> = {
  blood_pressure: '血压',
  blood_glucose: '血糖',
  weight: '体重',
  temperature: '体温',
};

// 频率展示
export const frequencyMap: Record<string, string> = {
  daily: '每日',
  weekly: '每周',
};

// 时段标签(fallback;正常应从后端 /api/monitoring-dict 获取)
export const timeSlotLabelMap: Record<string, string> = {
  fasting: '空腹',
  after_breakfast: '早餐后2小时',
  before_lunch: '午餐前',
  after_lunch: '午餐后2小时',
  before_dinner: '晚餐前',
  after_dinner: '晚餐后2小时',
  bedtime: '睡前',
  morning: '晨起',
  afternoon: '下午',
  evening: '晚上',
  anytime: '不定时',
};

// 按监测类型提供完整合法时段(fallback;正常应从后端字典获取)
export const monitoringSlotsByType: Record<
  string,
  { value: string; label: string }[]
> = {
  blood_glucose: [
    { value: 'fasting', label: timeSlotLabelMap.fasting },
    { value: 'after_breakfast', label: timeSlotLabelMap.after_breakfast },
    { value: 'before_lunch', label: timeSlotLabelMap.before_lunch },
    { value: 'after_lunch', label: timeSlotLabelMap.after_lunch },
    { value: 'before_dinner', label: timeSlotLabelMap.before_dinner },
    { value: 'after_dinner', label: timeSlotLabelMap.after_dinner },
    { value: 'bedtime', label: timeSlotLabelMap.bedtime },
  ],
  blood_pressure: [
    { value: 'morning', label: timeSlotLabelMap.morning },
    { value: 'afternoon', label: timeSlotLabelMap.afternoon },
    { value: 'evening', label: timeSlotLabelMap.evening },
    { value: 'bedtime', label: timeSlotLabelMap.bedtime },
  ],
  weight: [{ value: 'morning', label: timeSlotLabelMap.morning }],
  temperature: [
    { value: 'morning', label: timeSlotLabelMap.morning },
    { value: 'afternoon', label: timeSlotLabelMap.afternoon },
    { value: 'evening', label: timeSlotLabelMap.evening },
    { value: 'bedtime', label: timeSlotLabelMap.bedtime },
    { value: 'anytime', label: timeSlotLabelMap.anytime },
  ],
};

// region 用药提醒

// 与餐关系展示，取值与服务端 healthplan.MealRelation* 一致
export const medicationMealRelationMap: Record<string, string> = {
  before_meal: '餐前',
  with_meal: '随餐',
  after_meal: '餐后',
  bedtime: '睡前',
  empty_stomach: '空腹',
};

export const medicationMealRelationOptions = [
  { label: '未指定', value: '' },
  ...Object.entries(medicationMealRelationMap).map(([value, label]) => ({
    label,
    value,
  })),
];

// 全天 48 个半小时槽位，提醒时间下拉框只显示纯时刻，餐前餐后由「与餐关系」单独表达
export const medicationReminderTimeOptions: { value: string; label: string }[] =
  Array.from({ length: 48 }, (_, index) => {
    const value = `${String(Math.floor(index / 2)).padStart(2, '0')}:${index % 2 === 0 ? '00' : '30'}`;
    return { value, label: value };
  });

// 频次 → 默认提醒时间与与餐关系，与服务端 healthplan.frequencyReminderDefaults 保持一致
// 医生微调频次时自动填充，HIS 候选的默认值由服务端换算；时间个数同时决定后台显示几个时间点
const medicationReminderDefaultsByFrequency: Record<
  string,
  { times: string[]; mealRelation: MedicationMealRelation | '' }
> = {
  QD: { times: ['08:00'], mealRelation: '' },
  SQ1: { times: ['08:00'], mealRelation: '' },
  QM: { times: ['08:00'], mealRelation: '' },
  QAM: { times: ['08:00'], mealRelation: '' },
  ACQD: { times: ['08:00'], mealRelation: 'before_meal' },
  BID: { times: ['08:00', '20:00'], mealRelation: '' },
  Q12H: { times: ['08:00', '20:00'], mealRelation: '' },
  ACBID: { times: ['08:00', '18:30'], mealRelation: 'before_meal' },
  TID: { times: ['08:00', '12:30', '18:30'], mealRelation: '' },
  // Q?H 按等间隔换算；Q6H 等间隔必有一次落在凌晨，不自动填，交给医生
  Q8H: { times: ['06:00', '14:00', '22:00'], mealRelation: '' },
  ACTID: { times: ['08:00', '12:30', '18:30'], mealRelation: 'before_meal' },
  QID: { times: ['08:00', '12:30', '18:30', '22:00'], mealRelation: '' },
  QN: { times: ['21:30'], mealRelation: '' },
  QPM: { times: ['21:30'], mealRelation: '' },
  HS: { times: ['21:30'], mealRelation: 'bedtime' },
  QHS: { times: ['21:30'], mealRelation: 'bedtime' },
  // AC / PC 单独出现时次数不明，只给与餐关系，时间留给医生
  AC: { times: [], mealRelation: 'before_meal' },
  PC: { times: [], mealRelation: 'after_meal' },
};

// normalizeMedicationFrequency 与服务端同口径规范频次：大写、去空白、去掉「BID（口服）」这类括号后缀
function normalizeMedicationFrequency(frequency: string): string {
  const value = frequency.replace(/\s+/g, '').toUpperCase();
  const index = value.search(/[（(]/);
  return index >= 0 ? value.slice(0, index) : value;
}

// medicationReminderDefaults 按 HIS 频次给出建议的服药安排与提醒设置
//
// 换算不出来的频次（Q6H/PRN/QOD/QW 等）返回空剂次，也就是「尚未安排」，医生必须排出时间或选择按需服用才能批准
// 建议剂次不带 id：id 由服务端在写入前补齐，前端只负责把已有的 id 原样带回去
export function medicationReminderDefaults(frequency: string): {
  reminderEnabled: boolean;
  doses: MedicationDose[];
  mealRelation: MedicationMealRelation | '';
} {
  const defaults = medicationReminderDefaultsByFrequency[
    normalizeMedicationFrequency(frequency)
  ] ?? { times: [], mealRelation: '' as const };
  return {
    reminderEnabled: defaults.times.length > 0,
    doses: defaults.times.map((time) => ({ time })),
    mealRelation: defaults.mealRelation,
  };
}

// medicationReminderTimesCount 频次对应的每日服药次数；无法按天换算的频次（Q6H/PRN/QW 等）返回 0，由医生按需增删
export function medicationReminderTimesCount(frequency: string): number {
  return (
    medicationReminderDefaultsByFrequency[
      normalizeMedicationFrequency(frequency)
    ]?.times.length ?? 0
  );
}

// isMedicationAsNeeded 判断是不是按需服用：唯一一条剂次且没有时间点
export function isMedicationAsNeeded(doses: MedicationDose[]): boolean {
  return doses.length === 1 && !doses[0].time;
}

// medicationDoseTimes 取剂次里的时间点，按需服用的空时间点不计入
export function medicationDoseTimes(doses: MedicationDose[]): string[] {
  return doses.map((dose) => dose.time).filter(Boolean);
}

// endregion
